/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useEffect, useMemo, useState,
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Autocomplete,
  Skeleton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  FormDialogActions,
  FormElement,
  FormInputsStack,
  FormCancelButton,
  getSubmitButtonProps,
  OutlinedIcons,
  formatCountryCodesToString,
  getFormattedDateShortMonthDDYYYY,
  useApiClient,
  useFetchImmutable,
  useFetch,
} from '@marageti/z4-lib';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Ticket,
  CreateTicketRequest,
  UpdateTicketDetailsRequest,
} from '@marageti/z4-sdk/lib/tickets';
import { TripRequest } from '@marageti/z4-sdk/lib/travel';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import { SearchResult } from '@marageti/z4-sdk/lib/entity';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import TravelerLookupInput from '../traveler-lookup-input';

type AssigneeOption = {
  title: string;
  value: string;
  status?: string;
};

type TripRequestOption = {
  label: string;
  value: string;
};

type TravelerOption = {
  id: string;
  fullName: string;
  email: string;
} | null;

// Schema factory that conditionally requires tripRequest and traveler
const createSchema = (requireTripRequest: boolean, requireTraveler: boolean) => z.object({
  title: z.string().min(1, 'Title is required'),
  assignedTo: z.object({
    title: z.string(),
    value: z.string().min(1, 'Assignee is required'),
    status: z.string().optional(),
  }),
  dueAt: z.date({ required_error: 'Due date is required' }),
  description: z.string().optional(),
  traveler: requireTraveler
    ? z.object({
      id: z.string().min(1, 'Traveler is required'),
      fullName: z.string(),
      email: z.string(),
    })
    : z.object({
      id: z.string(),
      fullName: z.string(),
      email: z.string(),
    }).nullable().optional(),
  tripRequest: requireTripRequest
    ? z.object({
      label: z.string(),
      value: z.string().min(1, 'Trip Request is required'),
    })
    : z.object({
      label: z.string(),
      value: z.string(),
    }).optional(),
});

type FormData = {
  title: string;
  assignedTo: AssigneeOption;
  dueAt: Date;
  description: string;
  traveler?: TravelerOption;
  tripRequest?: TripRequestOption;
};

type ReminderEditModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (
    data: CreateTicketRequest | UpdateTicketDetailsRequest,
    selectedTripRequestId?: string,
    selectedTravelerId?: string
  ) => Promise<void>;
  ticket?: Ticket;
  defaultValues?: {
    title?: string;
    description?: string;
    dueAt?: string;
    assignedTo?: string;
  };
  assignees: AssigneeOption[];
  isNew?: boolean;
  /** Trip requests for the traveler - shown when creating a new reminder without a pre-selected TR */
  tripRequests?: TripRequest[];
  /** Whether a trip request ID is already associated (e.g., from TR page context) */
  hasTripRequestContext?: boolean;
  /** Country codes map for formatting destinations - passed from hook to avoid duplicate fetch */
  countryCodes?: CountryCodeMap;
  /** Whether trip requests are still loading */
  isLoadingTripRequests?: boolean;
  /** Trip request ID to pre-select when duplicating a reminder */
  duplicateTripRequestId?: string;
  /** Whether traveler selection is needed (e.g., from global reminders page) */
  needsTravelerSelection?: boolean;
};

const getModalTitle = (isNew: boolean, ticket?: Ticket): string => {
  if (isNew) return 'New Reminder';
  if (ticket) return 'Edit Reminder';
  return 'New Reminder';
};

const ReminderEditModal = ({
  open,
  onClose,
  onSave,
  ticket,
  defaultValues,
  assignees,
  isNew = false,
  tripRequests: tripRequestsProp,
  hasTripRequestContext = false,
  countryCodes: countryCodesProp,
  isLoadingTripRequests: isLoadingTripRequestsProp = false,
  duplicateTripRequestId,
  needsTravelerSelection = false,
}: ReminderEditModalProps) => {
  const apiClient = useApiClient();

  // Selected traveler state (managed outside of form for trip request fetching)
  const [selectedTraveler, setSelectedTraveler] = useState<Traveler | undefined>(undefined);

  // Use countryCodes from props if available, otherwise fetch them (fallback for backward compatibility)
  const { data: countryCodesFetched } = useFetchImmutable<CountryCodeMap>(
    apiClient.standardsClient.getCountryCodes,
    [],
    !countryCodesProp, // Only fetch if not provided via props
  );
  const countryCodes = countryCodesProp || countryCodesFetched;

  // Fetch trip requests for selected traveler (when needsTravelerSelection is true)
  const {
    data: travelerTripRequestsData,
    isLoading: isLoadingTravelerTripRequests,
  } = useFetch<SearchResult<TripRequest>>(
    apiClient.tripRequestsClient.postTripRequestSearches,
    [{
      filterBy: [{ field: 'travelers.id', value: [selectedTraveler?.id || ''] }],
      sortBy: [{ field: 'createdAt', order: 'desc' }],
      pagination: { limit: 100, page: 1 },
    }],
    needsTravelerSelection && !!selectedTraveler?.id,
  );

  // Use trip requests from props (from traveler profile) or from selected traveler
  const tripRequests = needsTravelerSelection
    ? travelerTripRequestsData?.hits
    : tripRequestsProp;
  const isLoadingTripRequests = needsTravelerSelection
    ? isLoadingTravelerTripRequests
    : isLoadingTripRequestsProp;

  // Determine if we need trip request selection
  const needsTripRequestSelection = isNew && !hasTripRequestContext
    && (!!tripRequestsProp || needsTravelerSelection);
  const hasTripRequestOptions = tripRequests && tripRequests.length > 0;
  const showTripRequestField = needsTripRequestSelection
    && (!needsTravelerSelection || !!selectedTraveler);
  const showNoTripRequestsMessage = needsTripRequestSelection
    && !!selectedTraveler
    && !isLoadingTripRequests
    && !hasTripRequestOptions;

  // Format trip requests as dropdown options: [Destination(s)] - [TR_Departure_Date]
  const tripRequestOptions = useMemo<TripRequestOption[]>(() => {
    if (!tripRequests || !countryCodes) return [];
    return tripRequests.map((tr) => {
      const destinations = formatCountryCodesToString(tr.countries, countryCodes);
      const departureDate = getFormattedDateShortMonthDDYYYY(tr.departureDate);
      return {
        label: `${destinations} - ${departureDate}`,
        value: tr.id,
      };
    });
  }, [tripRequests, countryCodes]);

  // Memoize initial values to avoid recreating on each render
  const initialTripRequest = useMemo((): TripRequestOption | undefined => {
    if (showTripRequestField && tripRequestOptions.length > 0) {
      // When duplicating, pre-select the TR from the original reminder
      if (duplicateTripRequestId) {
        const duplicateTR = tripRequestOptions.find((tr) => tr.value === duplicateTripRequestId);
        if (duplicateTR) return duplicateTR;
      }
      // Otherwise, select the most recent TR (first one since sorted by createdAt desc)
      return tripRequestOptions[0];
    }
    return undefined;
  }, [showTripRequestField, tripRequestOptions, duplicateTripRequestId]);

  const initialAssignedTo = useMemo((): AssigneeOption => {
    const assignedToId = defaultValues?.assignedTo || ticket?.assignees?.[0]?.id;
    if (assignedToId) {
      const found = assignees.find((a) => a.value === assignedToId);
      if (found) return found;
    }
    const fromTicket = ticket?.assignees?.[0];
    if (fromTicket?.id && fromTicket.name) {
      return { title: fromTicket.name, value: fromTicket.id };
    }
    return { title: '', value: '' };
  }, [defaultValues?.assignedTo, ticket?.assignees, assignees]);

  const initialDueAt = useMemo((): Date | undefined => {
    const dateStr = defaultValues?.dueAt || ticket?.dueAt;
    if (dateStr) {
      return new Date(dateStr);
    }
    return undefined;
  }, [defaultValues?.dueAt, ticket?.dueAt]);

  // Create schema based on whether trip request and traveler selection is required
  const schema = useMemo(
    () => createSchema(
      showTripRequestField && !!hasTripRequestOptions,
      needsTravelerSelection,
    ),
    [showTripRequestField, hasTripRequestOptions, needsTravelerSelection],
  );

  const {
    control,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    reset,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: defaultValues?.title || ticket?.title || '',
      assignedTo: initialAssignedTo,
      dueAt: initialDueAt,
      description: defaultValues?.description || ticket?.description || '',
      traveler: null,
      tripRequest: initialTripRequest,
    },
  });

  // Reset form only when modal opens
  useEffect(() => {
    if (open) {
      reset({
        title: defaultValues?.title || ticket?.title || '',
        assignedTo: initialAssignedTo,
        dueAt: initialDueAt,
        description: defaultValues?.description || ticket?.description || '',
        traveler: null,
        tripRequest: undefined,
      });
      setSelectedTraveler(undefined);
    }
  }, [open]);

  // Auto-select first trip request when traveler's trip requests load
  useEffect(() => {
    if (needsTravelerSelection && selectedTraveler && tripRequestOptions.length > 0) {
      setValue('tripRequest', tripRequestOptions[0]);
    }
  }, [needsTravelerSelection, selectedTraveler, tripRequestOptions, setValue]);

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const requestData: Partial<CreateTicketRequest> &
    Partial<UpdateTicketDetailsRequest> = {
      title: data.title,
      assignees: [{ id: data.assignedTo.value, name: data.assignedTo.title }],
      dueAt: data.dueAt.toISOString(),
      description: data.description || '',
    };

    const selectedTripRequestId = data.tripRequest?.value;
    const selectedTravelerId = selectedTraveler?.id;
    await onSave(
      requestData as CreateTicketRequest | UpdateTicketDetailsRequest,
      selectedTripRequestId,
      selectedTravelerId,
    );
  };

  const modalTitle = getModalTitle(isNew, ticket);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pt: 4, px: 3 }}>{modalTitle}</DialogTitle>

      <FormElement name="reminder-edit-form" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ paddingTop: 1 }}>
          <FormInputsStack>
            {/* Title */}
            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title"
                  fullWidth
                  required
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              )}
            />

            {/* Description */}
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={6}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />

            {/* Traveler - only shown when creating new reminder from TR Reminders page */}
            {needsTravelerSelection && (
              <TravelerLookupInput
                key={selectedTraveler?.id}
                value={selectedTraveler}
                onChange={(_: any, value: Traveler | null) => {
                  setSelectedTraveler(value || undefined);
                  setValue('traveler', value ? {
                    id: value.id,
                    fullName: value.fullName,
                    email: value.email,
                  } : null);
                  setValue('tripRequest', undefined);
                }}
                inputProps={{
                  required: true,
                  error: !!errors.traveler,
                  helperText: (errors.traveler as { id?: { message?: string } } | undefined)?.id?.message
                    || (errors.traveler as { message?: string } | undefined)?.message,
                }}
              />
            )}

            {/* Trip Request - Disabled state when no traveler selected */}
            {needsTravelerSelection && !selectedTraveler && (
              <TextField
                label="Trip Request"
                required
                disabled
                value=""
                fullWidth
              />
            )}

            {/* Trip Request - Loading state */}
            {showTripRequestField && isLoadingTripRequests && (
              <Skeleton variant="rounded" height={56} />
            )}

            {/* Trip Request - only shown when creating new reminder without TR context */}
            {showTripRequestField && !isLoadingTripRequests && hasTripRequestOptions && (
              <Controller
                control={control}
                name="tripRequest"
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    getOptionKey={(o) => o.value}
                    getOptionLabel={(o) => o.label}
                    id="reminder-trip-request"
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    onChange={(_, v) => field.onChange(v || { label: '', value: '' })}
                    options={tripRequestOptions}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Trip Request"
                        required
                        error={!!errors.tripRequest}
                        helperText={
                          errors.tripRequest?.value?.message
                          || (errors.tripRequest as { message?: string } | undefined)?.message
                        }
                      />
                    )}
                    value={field.value || null}
                  />
                )}
              />
            )}

            {/* Message when traveler has no trip requests */}
            {showNoTripRequestsMessage && !isLoadingTripRequests && (
              <TextField
                label="Trip Request"
                required
                disabled
                value=""
                helperText="This traveler has no trip requests. Please create a trip request first."
                error
                fullWidth
              />
            )}

            {/* Assignee */}
            <Controller
              control={control}
              name="assignedTo"
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  getOptionKey={(o) => o.value}
                  getOptionLabel={(o) => o.title}
                  groupBy={(o) => o.status || ''}
                  id="reminder-assignee"
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  onChange={(_, v) => field.onChange(v || { title: '', value: '' })}
                  options={assignees.filter((a) => a.value !== '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assignee"
                      required
                      error={!!errors.assignedTo}
                      helperText={errors.assignedTo?.value?.message}
                    />
                  )}
                  value={field.value}
                />
              )}
            />

            {/* Due Date */}
            <Controller
              control={control}
              name="dueAt"
              render={({ field }) => (
                <DatePicker
                  label="Due Date"
                  value={field.value || null}
                  onChange={(newValue) => field.onChange(newValue)}
                  slots={{
                    openPickerIcon: OutlinedIcons.OutlinedCalendarIcon,
                  }}
                  slotProps={{
                    openPickerIcon: {
                      color: 'primary',
                    },
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!errors.dueAt,
                      helperText: errors.dueAt?.message,
                    },
                  }}
                />
              )}
            />
          </FormInputsStack>
        </DialogContent>
        <FormDialogActions sx={{
          mt: 0, border: 0, p: 4,
        }}
        >
          <FormCancelButton onClick={onClose} />
          <LoadingButton
            variant="contained"
            type="submit"
            {...getSubmitButtonProps(
              isSubmitting,
              isValid
                && !showNoTripRequestsMessage
                && !isLoadingTripRequests
                && (!needsTravelerSelection || !!selectedTraveler),
            )}
          >
            {isNew ? 'Create' : 'Save'}
          </LoadingButton>
        </FormDialogActions>
      </FormElement>
    </Dialog>
  );
};

export default ReminderEditModal;
